import { Slot } from '@radix-ui/react-slot';
import type * as React from 'react';
import { cn } from '../../styles/lib/utils';
import { type AvatarMember, type AvatarUser, getAvatarLetters } from '../../utils';
import { Text } from '../Text';

function AvatarData({
  className,
  user,
  initials = false,
  size = 'large',
  asChild = false,
  ...props
}: React.ComponentProps<'div'> & {
  user: AvatarUser | AvatarMember;
  initials?: boolean;
  size?: 'large' | 'small';
  asChild?: boolean;
}) {
  const Comp = asChild ? Slot : 'div';
  return (
    <Comp data-slot="avatar-data" className={cn('flex items-center gap-1 w-full', className)} {...props}>
      <AvatarIcon user={user} asChild={asChild} size={size} />
      <AvatarName user={user} initials={initials} />
    </Comp>
  );
}

function getAvatarName(user: AvatarUser | AvatarMember) {
  return typeof user === 'object' && 'name' in user ? user.name : `${user.firstName} ${user.lastName}`;
}

function AvatarIcon({
  className,
  user,
  size = 'large',
  asChild = false,
  announceName = false,
  ...props
}: React.ComponentProps<'div'> & {
  user: AvatarUser | AvatarMember;
  size?: 'large' | 'small';
  asChild?: boolean;
  /** Names the avatar for screen readers. Leave off when the name is already shown next to it. */
  announceName?: boolean;
}) {
  const Comp = asChild ? Slot : 'div';
  const name = getAvatarName(user);

  return (
    <Comp
      data-slot="avatar-icon"
      {...(announceName && { role: 'img', 'aria-label': name.trim() || user.email })}
      className={cn(
        'relative flex shrink-0 items-center justify-center text-grey-90 rounded-full overflow-hidden bg-grey-20',
        size === 'large' ? 'h-7 w-7' : 'h-5 w-5',
        className,
      )}
      {...props}
    >
      {user.url ? (
        <img
          src={user.url}
          alt={announceName ? '' : name}
          className="absolute inset-0 size-full object-cover"
          onError={(event) => {
            event.currentTarget.style.display = 'none';
          }}
        />
      ) : (
        <Text className={cn('text-xs font-bold uppercase', size === 'large' ? 'text-sm' : 'text-[10px]')}>
          {getAvatarLetters(user)}
        </Text>
      )}
    </Comp>
  );
}

function AvatarName({
  className,
  user,
  initials = false,
  ...props
}: React.ComponentProps<'div'> & { user: AvatarUser | AvatarMember; initials?: boolean }) {
  return (
    <Text data-slot="avatar-name" className="min-w-0 text-xs truncate" {...props}>
      {initials ? getAvatarLetters(user) : getAvatarName(user)}
    </Text>
  );
}

export { AvatarData, AvatarIcon, AvatarName };
